import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, ArrowRight, Home, User, PoundSterling, Loader2, Search, CheckCircle, Building, FileText
} from "lucide-react";
import { API } from "@/App";
import PostcodeLookup from "@/components/PostcodeLookup";

export const NewInstructionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get("vendor");
  const applicantId = searchParams.get("applicant_id");
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [applicant, setApplicant] = useState(null);
  const [step, setStep] = useState(1); // 1: Property, 2: Pricing/Fees, 3: Confirm

  // Property details
  const [property, setProperty] = useState({
    address_line1: "",
    address_line2: "",
    town: "",
    county: "",
    postcode: "",
    property_type: "detached",
    bedrooms: "",
    bathrooms: "",
    reception_rooms: "",
    tenure: "freehold",
    epc_rating: "",
    description: "",
  });

  // Pricing details
  const [pricing, setPricing] = useState({
    asking_price: "",
    price_type: "", // offers_over, offers_in_region, offers_in_excess, guide_price
    valuation_price: "",
    fee_type: "percentage", // percentage or fixed
    fee_percentage: "1.5",
    fee_fixed: "",
    marketing_contribution: "",
  });

  useEffect(() => {
    loadInitialData();
  }, [vendorId, applicantId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (vendorId) {
        const res = await axios.get(`${API}/sales/vendors/${vendorId}`, { headers });
        setVendor(res.data.vendor);
      }
      
      if (applicantId) {
        const res = await axios.get(`${API}/applicants/details/${applicantId}`, { headers });
        setApplicant(res.data.applicant || res.data);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateFee = () => {
    const price = parseFloat(pricing.asking_price) || 0;
    if (pricing.fee_type === "percentage") {
      const percentage = parseFloat(pricing.fee_percentage) || 0;
      return (price * percentage / 100).toFixed(2);
    }
    return pricing.fee_fixed || "0";
  };

  const handleSubmit = async () => {
    if (!property.address_line1 || !property.postcode) {
      toast.error("Please enter property address");
      return;
    }
    if (!pricing.asking_price) {
      toast.error("Please enter asking price");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const fullAddress = [
        property.address_line1,
        property.address_line2,
        property.town,
        property.county,
        property.postcode
      ].filter(Boolean).join(", ");

      // Create the sales instruction
      const instructionData = {
        // Property
        property_address: fullAddress,
        address: fullAddress,
        address_line1: property.address_line1,
        address_line2: property.address_line2,
        town: property.town,
        county: property.county,
        postcode: property.postcode,
        property_type: property.property_type,
        bedrooms: parseInt(property.bedrooms) || null,
        bathrooms: parseInt(property.bathrooms) || null,
        reception_rooms: parseInt(property.reception_rooms) || null,
        tenure: property.tenure,
        epc_rating: property.epc_rating,
        description: property.description,
        
        // Pricing
        price: parseFloat(pricing.asking_price) || 0,
        asking_price: parseFloat(pricing.asking_price) || 0,
        price_type: pricing.price_type || "",
        valuation_price: parseFloat(pricing.valuation_price) || null,
        fee: parseFloat(calculateFee()) || 0,
        fee_type: pricing.fee_type,
        fee_percentage: pricing.fee_type === "percentage" ? parseFloat(pricing.fee_percentage) : null,
        marketing_contribution: parseFloat(pricing.marketing_contribution) || 0,
        
        // Seller (from vendor)
        seller_contact_id: vendor?.contact_id || vendorId,
        seller_name: vendor?.name || "",
        seller_email: vendor?.email || "",
        seller_phone: vendor?.phone || vendor?.mobile_phone || "",
        
        // Status - new instruction starts as active
        status: "active",
        current_stage: "instruction_taken",
      };

      // Use the new instructions endpoint
      const res = await axios.post(`${API}/sales/instructions`, instructionData, { headers });
      
      toast.success("New instruction created successfully!");
      navigate(`/sales/case/${res.data.case_id}/documents`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create instruction");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/sales")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sales
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-purple-700">New Sales Instruction</h1>
                <p className="text-sm text-slate-500">
                  {vendor ? `For vendor: ${vendor.name}` : "Create a new property listing"}
                </p>
              </div>
            </div>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-4 mt-6">
            {[
              { num: 1, label: "Property Details" },
              { num: 2, label: "Pricing & Fees" },
              { num: 3, label: "Confirm" }
            ].map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center gap-2 ${step >= s.num ? "text-purple-600" : "text-slate-400"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step > s.num ? "bg-purple-600 text-white" :
                    step === s.num ? "bg-purple-100 text-purple-700 border-2 border-purple-600" :
                    "bg-slate-100 text-slate-400"
                  }`}>
                    {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="font-medium hidden sm:block">{s.label}</span>
                </div>
                {idx < 2 && <div className={`w-12 h-0.5 mx-2 ${step > s.num ? "bg-purple-600" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* Step 1: Property Details */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                Property Details
              </CardTitle>
              <CardDescription>Enter the property information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Address */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-700">Address</h3>
                
                {/* Postcode Lookup Component */}
                <PostcodeLookup 
                  onAddressSelect={(addr) => {
                    setProperty({
                      ...property,
                      address_line1: addr.line_1 || "",
                      address_line2: addr.line_2 || "",
                      town: addr.town || "",
                      county: addr.county || "",
                      postcode: addr.postcode || ""
                    });
                    toast.success("Address selected!");
                  }}
                />
                
                <div>
                  <Label>Address Line 1 *</Label>
                  <Input 
                    value={property.address_line1}
                    onChange={(e) => setProperty({...property, address_line1: e.target.value})}
                    placeholder="e.g. 123 High Street"
                  />
                </div>
                <div>
                  <Label>Address Line 2</Label>
                  <Input 
                    value={property.address_line2}
                    onChange={(e) => setProperty({...property, address_line2: e.target.value})}
                    placeholder="e.g. Apartment 4"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Town/City</Label>
                    <Input 
                      value={property.town}
                      onChange={(e) => setProperty({...property, town: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>County</Label>
                    <Input 
                      value={property.county}
                      onChange={(e) => setProperty({...property, county: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Postcode *</Label>
                    <Input 
                      value={property.postcode}
                      onChange={(e) => setProperty({...property, postcode: e.target.value.toUpperCase()})}
                      placeholder="e.g. MK42 9AB"
                    />
                  </div>
                </div>
              </div>

              {/* Property Info */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-slate-700">Property Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Property Type</Label>
                    <Select value={property.property_type} onValueChange={(v) => setProperty({...property, property_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="detached">Detached</SelectItem>
                        <SelectItem value="semi-detached">Semi-Detached</SelectItem>
                        <SelectItem value="terraced">Terraced</SelectItem>
                        <SelectItem value="flat">Flat/Apartment</SelectItem>
                        <SelectItem value="bungalow">Bungalow</SelectItem>
                        <SelectItem value="maisonette">Maisonette</SelectItem>
                        <SelectItem value="land">Land</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tenure</Label>
                    <Select value={property.tenure} onValueChange={(v) => setProperty({...property, tenure: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="freehold">Freehold</SelectItem>
                        <SelectItem value="leasehold">Leasehold</SelectItem>
                        <SelectItem value="share_of_freehold">Share of Freehold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>EPC Rating</Label>
                    <Select value={property.epc_rating} onValueChange={(v) => setProperty({...property, epc_rating: v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {["A", "B", "C", "D", "E", "F", "G"].map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Bedrooms</Label>
                    <Input 
                      type="number"
                      value={property.bedrooms}
                      onChange={(e) => setProperty({...property, bedrooms: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Bathrooms</Label>
                    <Input 
                      type="number"
                      value={property.bathrooms}
                      onChange={(e) => setProperty({...property, bathrooms: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Reception Rooms</Label>
                    <Input 
                      type="number"
                      value={property.reception_rooms}
                      onChange={(e) => setProperty({...property, reception_rooms: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-slate-700">Description</h3>
                <Textarea 
                  value={property.description}
                  onChange={(e) => setProperty({...property, description: e.target.value})}
                  placeholder="Brief property description..."
                  rows={4}
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={() => setStep(2)} className="bg-purple-600 hover:bg-purple-700">
                  Next: Pricing & Fees
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Pricing & Fees */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="w-5 h-5" />
                Pricing & Fees
              </CardTitle>
              <CardDescription>Set the asking price and your fees</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Pricing */}
              <div className="space-y-4">
                <h3 className="font-medium text-slate-700">Pricing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Asking Price *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                      <Input 
                        type="number"
                        value={pricing.asking_price}
                        onChange={(e) => setPricing({...pricing, asking_price: e.target.value})}
                        className="pl-7"
                        placeholder="e.g. 350000"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Price Type</Label>
                    <Select 
                      value={pricing.price_type} 
                      onValueChange={(v) => setPricing({...pricing, price_type: v})}
                    >
                      <SelectTrigger><SelectValue placeholder="Select price type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Fixed Price</SelectItem>
                        <SelectItem value="offers_over">Offers Over</SelectItem>
                        <SelectItem value="offers_in_region">Offers in Region of</SelectItem>
                        <SelectItem value="offers_in_excess">Offers in Excess of</SelectItem>
                        <SelectItem value="guide_price">Guide Price</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Valuation Price (if different)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                    <Input 
                      type="number"
                      value={pricing.valuation_price}
                      onChange={(e) => setPricing({...pricing, valuation_price: e.target.value})}
                      className="pl-7"
                    />
                  </div>
                </div>
              </div>

              {/* Fees */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium text-slate-700">Agency Fee</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fee Type</Label>
                    <Select value={pricing.fee_type} onValueChange={(v) => setPricing({...pricing, fee_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage of Sale Price</SelectItem>
                        <SelectItem value="fixed">Fixed Fee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {pricing.fee_type === "percentage" ? (
                    <div>
                      <Label>Fee Percentage (%)</Label>
                      <Input 
                        type="number"
                        step="0.1"
                        value={pricing.fee_percentage}
                        onChange={(e) => setPricing({...pricing, fee_percentage: e.target.value})}
                        placeholder="e.g. 1.5"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label>Fixed Fee</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                        <Input 
                          type="number"
                          value={pricing.fee_fixed}
                          onChange={(e) => setPricing({...pricing, fee_fixed: e.target.value})}
                          className="pl-7"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Marketing Contribution</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                    <Input 
                      type="number"
                      value={pricing.marketing_contribution}
                      onChange={(e) => setPricing({...pricing, marketing_contribution: e.target.value})}
                      className="pl-7"
                      placeholder="e.g. 250"
                    />
                  </div>
                </div>

                {/* Fee Calculation */}
                {pricing.asking_price && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-700">
                      Estimated Fee: <strong>£{parseFloat(calculateFee()).toLocaleString()}</strong>
                      {pricing.fee_type === "percentage" && (
                        <span className="text-purple-500"> ({pricing.fee_percentage}% of £{parseFloat(pricing.asking_price).toLocaleString()})</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => setStep(3)} className="bg-purple-600 hover:bg-purple-700">
                  Next: Confirm
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Confirm Instruction
              </CardTitle>
              <CardDescription>Review and confirm the new instruction</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-6">
                {/* Property Summary */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Property
                  </h3>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    <p className="font-medium">{property.address_line1}</p>
                    {property.address_line2 && <p className="text-sm text-slate-600">{property.address_line2}</p>}
                    <p className="text-sm text-slate-600">{property.town}, {property.postcode}</p>
                    <div className="pt-2 border-t mt-2 text-sm">
                      <p><span className="text-slate-500">Type:</span> {property.property_type}</p>
                      <p><span className="text-slate-500">Bedrooms:</span> {property.bedrooms || "-"}</p>
                      <p><span className="text-slate-500">Tenure:</span> {property.tenure}</p>
                    </div>
                  </div>
                </div>

                {/* Vendor Summary */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Vendor
                  </h3>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    {vendor ? (
                      <>
                        <p className="font-medium">{vendor.name}</p>
                        {vendor.email && <p className="text-sm text-slate-600">{vendor.email}</p>}
                        {vendor.phone && <p className="text-sm text-slate-600">{vendor.phone}</p>}
                      </>
                    ) : (
                      <p className="text-slate-400 italic">No vendor selected</p>
                    )}
                  </div>
                </div>

                {/* Pricing Summary */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <PoundSterling className="w-4 h-4" />
                    Pricing
                  </h3>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2 text-sm">
                    <p className="text-lg font-bold text-purple-700">
                      £{parseFloat(pricing.asking_price || 0).toLocaleString()}
                    </p>
                    {pricing.valuation_price && (
                      <p><span className="text-slate-500">Valuation:</span> £{parseFloat(pricing.valuation_price).toLocaleString()}</p>
                    )}
                    <p className="pt-2 border-t mt-2">
                      <span className="text-slate-500">Agency Fee:</span>{" "}
                      <strong>£{parseFloat(calculateFee()).toLocaleString()}</strong>
                      {pricing.fee_type === "percentage" && ` (${pricing.fee_percentage}%)`}
                    </p>
                    {pricing.marketing_contribution && (
                      <p><span className="text-slate-500">Marketing:</span> £{parseFloat(pricing.marketing_contribution).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Create Instruction
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default NewInstructionPage;
